import { Component, OnInit, OnDestroy, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { KeycloakService, KeycloakEventType } from 'keycloak-angular';
import { Subscription } from 'rxjs';
import { AssetHeaderConfig } from '../../shared/components/asset-header/asset-header.component';
import { FilterConfig } from '../../shared/components/asset-filter/asset-filter.component';
import { FeatureCardData } from '../../shared/components/feature-card/feature-card.component';
import { AssetListingPageComponent } from '../../shared/components/asset-listing-page/asset-listing-page.component';
import { LoginModalComponent } from '../../shared/components/login-modal/login-modal.component';
import { CatalogueApiService, SearchCriteria } from '@angular-workspace/services';
import { FormatService } from '../../shared/services/format.service';
import { HelperService } from '../../shared/services/helper.service';

@Component({
  selector: 'app-datasets',
  standalone: true,
  imports: [
    CommonModule,
    AssetListingPageComponent,
    LoginModalComponent
  ],
  templateUrl: './datasets.component.html',
  styleUrl: './datasets.component.scss',
})
export class DatasetsComponent implements OnInit, OnDestroy {
  headerConfig: AssetHeaderConfig = {
    breadcrumbs: [
      { label: 'Home', route: '/' },
      { label: 'Datasets' }
    ],
    title: 'Datasets',
    description: 'Explore a collection of datasets to support analysis, research, and solution development'
  };

  filterConfig: FilterConfig = {
    sections: [
      {
        id: 'bookmarks',
        title: 'My Bookmarks',
        type: 'checkbox',
        isExpanded: false,
        icon: '/filters/hash.svg', // Using hash icon as placeholder, will be replaced with lock when not logged in
        options: []
      },
      {
        id: 'concepts',
        title: 'Concepts',
        type: 'checkbox',
        searchPlaceholder: 'Search for concepts',
        isExpanded: false,
        icon: '/filters/leaf.svg',
        options: []
      },
      {
        id: 'organisation',
        title: 'Organisation',
        type: 'checkbox',
        isExpanded: false,
        icon: '/filters/building-2.svg',
        options: []
      },
      {
        id: 'organisation-type',
        title: 'Organisation type',
        type: 'checkbox',
        isExpanded: false,
        icon: '/filters/people.svg',
        options: []
      },
      {
        id: 'tags',
        title: 'Tags',
        type: 'checkbox',
        searchPlaceholder: 'Search for tags',
        isExpanded: false,
        icon: '/filters/hash.svg',
        options: []
      },
      {
        id: 'file-format',
        title: 'File format',
        type: 'checkbox',
        isExpanded: false,
        icon: '/filters/file-digit.svg',
        options: []
      },
      {
        id: 'permissions',
        title: 'Permissions',
        type: 'checkbox',
        isExpanded: false,
        icon: '/filters/lock.svg',
        options: []
      },
      {
        id: 'last-updated',
        title: 'Last Updated At',
        type: 'radio',
        isExpanded: false,
        icon: '/filters/calendar.svg',
        options: [
          { id: '1-day', label: '1 Day ago', checked: false },
          { id: '1-week', label: '1 week ago', checked: false },
          { id: '1-month', label: '1 month ago', checked: false },
          { id: '1-year', label: '1 year ago', checked: false }
        ]
      }
    ],
    activeFilterCount: 0
  };

  datasets: FeatureCardData[] = [];
  currentPage = 1;
  totalPages = 0; // Will be updated dynamically from API response
  viewMode: 'grid' | 'list' | 'table' = 'grid';
  itemsPerPage = 6;
  searchQuery: string = ''; // Store search query
  sortOption: string = 'new-to-old'; // A-Z, Z-A, new-to-old, old-to-new
  isMobileFilterOpen = false; // Track mobile filter modal state - set to true to test modal rendering
  isLoggedIn: boolean = false;
  showLoginModal: boolean = false;

  private keycloak = inject(KeycloakService);
  private keycloakSubscription?: Subscription;

  constructor(
    private catalogueApiService: CatalogueApiService,
    private formatService: FormatService,
    private helperService: HelperService,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef
  ) {}

  async ngOnInit(): Promise<void> {
    // Check login status
    await this.checkLoginStatus();

    // Subscribe to Keycloak events to update login status dynamically
    this.keycloakSubscription = this.keycloak.keycloakEvents$.subscribe(
      async (event: unknown) => {
        const keycloakEvent = event as { type: KeycloakEventType };
        if (keycloakEvent.type === KeycloakEventType.OnAuthSuccess) {
          // User has logged in, update login status
          this.isLoggedIn = true;
          console.log('User logged in, updating login status');
        } else if (
          keycloakEvent.type === KeycloakEventType.OnAuthRefreshSuccess ||
          keycloakEvent.type === KeycloakEventType.OnAuthLogout
        ) {
          // User has logged out or token refreshed, update login status
          await this.checkLoginStatus();
        }
      }
    );

    // Check for search query from route parameters
    this.route.queryParams.subscribe(params => {
      if (params['search']) {
        this.searchQuery = params['search'];
      }
    });

    // Load datasets immediately (main content)
    // Load filter options in parallel (can load in background)
    this.loadDatasets();
    this.loadFilterOptions(); // Don't await - let it load in background
    this.updateActiveFilterCount();
  }

  ngOnDestroy(): void {
    // Unsubscribe from Keycloak events to prevent memory leaks
    if (this.keycloakSubscription) {
      this.keycloakSubscription.unsubscribe();
    }
  }

  async checkLoginStatus(): Promise<void> {
    try {
      this.isLoggedIn = await this.keycloak.isLoggedIn();
    } catch (error) {
      console.error('Error checking login status:', error);
      this.isLoggedIn = false;
    }
  }

  onBookmarksClick(): void {
    if (!this.isLoggedIn) {
      this.showLoginModal = true;
    }
  }

  onLikeClick(): void {
    if (!this.isLoggedIn) {
      this.showLoginModal = true;
    }
  }

  async loadDatasets(): Promise<void> {
    try {
      // Build searchCriteria from filters
      const searchCriteria = this.buildSearchCriteriaFromFilters();
      
      // Map sort option to API sort string
      const sortString = this.getSortStringFromOption(this.sortOption);

      // Call searchAllAssets with search query and filters
      // assetTypes: ['Dataset'] will be mapped to ['adex:DataBank'] by the service
      const response = await this.catalogueApiService.searchAllAssets(
        this.searchQuery || '', // searchQuery - use stored search query or empty string
        ['Dataset'], // assetTypes - will be mapped to adex:DataBank
        [], // customFilter - empty array, no filter fields specified
        this.currentPage, // page - use current page
        this.itemsPerPage, // size - use items per page
        sortString, // sort - from sort dropdown
        searchCriteria // additional searchCriteria from filters
      );

      // Debug: Log response structure
      console.log('API Response:', response);
      
      // Extract items from response (same approach as Features component)
      const items = this.extractItemsFromResponse(response);
      console.log('Extracted items:', items);
      console.log('Items count:', items.length);
      
      // Map API response to datasets - create a new array reference to ensure change detection
      this.datasets = [...items.map(item => this.mapApiItemToFeatureCard(item))];
      console.log('Mapped datasets:', this.datasets);
      console.log('Datasets count:', this.datasets.length);
      
      // Update pagination from paginationInfo (dynamic based on API response)
      this.updatePaginationFromResponse(response);
      
      // Trigger change detection to ensure the view updates after async operation
      this.cdr.detectChanges();
    } catch (error) {
      // Error is already handled by NetworkService (toaster shown)
      console.error('Failed to load datasets:', error);
      // Fallback to mock data on error
      this.loadMockDatasets();
    }
  }

  /**
   * Extract items array from API response (same approach as Features component)
   */
  private extractItemsFromResponse(response: any): any[] {
    // Handle null/undefined response
    if (!response) {
      console.warn('Response is null or undefined');
      return [];
    }

    // Direct array response
    if (Array.isArray(response)) {
      return response;
    }

    // Check for result array (most common structure: { result: [...], paginationInfo: {...} })
    if (response.result) {
      if (Array.isArray(response.result)) {
        return response.result;
      }
      // Handle nested result.results
      if (response.result.results && Array.isArray(response.result.results)) {
        return response.result.results;
      }
    }

    // Check for data array
    if (response.data && Array.isArray(response.data)) {
      return response.data;
    }

    // Check for items array
    if (response.items && Array.isArray(response.items)) {
      return response.items;
    }

    // Log warning if no items found
    console.warn('No items array found in response. Response structure:', Object.keys(response));
    return [];
  }

  /**
   * Map API item to FeatureCardData format (same as Features component)
   */
  private mapApiItemToFeatureCard(item: any): FeatureCardData {
    // Extract basic fields
    const id = item.id || '';
    const title = item.label || item.name || 'Untitled';
    const organization = item.provider || item.organisation || item.organization || item.provider_name || '';
    // Short description: prefer shortDescription, else truncate description (e.g. 120 chars)
    const rawDesc = item.shortDescription ?? item.short_description ?? item.description ?? '';
    const shortDescription = typeof rawDesc === 'string' && rawDesc.trim()
      ? (rawDesc.length > 120 ? rawDesc.trim().slice(0, 120) + '…' : rawDesc.trim())
      : undefined;
    
    // Extract ResourceServer names for card display (below organization)
    const resourceServerTags: string[] = [];
    // Extract server access types (ATTR, TEMPORAL, etc.) from resourceServer.accessTypes
    const serverAccessTypes: string[] = [];

    if (item.resourceServer) {
      const servers = Array.isArray(item.resourceServer) ? item.resourceServer : [item.resourceServer];
      servers.forEach((server: any) => {
        // Extract name from resource server
        if (server.name && !resourceServerTags.includes(server.name.toUpperCase())) {
          resourceServerTags.push(server.name.toUpperCase());
        }
        // If no name, try type
        if (server.type && !resourceServerTags.includes(server.type.toUpperCase())) {
          resourceServerTags.push(server.type.toUpperCase());
        }
        // Extract access types from each server
        if (Array.isArray(server.accessTypes)) {
          server.accessTypes.forEach((t: string) => {
            const val = String(t || '').trim().toUpperCase();
            if (val && !serverAccessTypes.includes(val)) {
              serverAccessTypes.push(val);
            }
          });
        }
      });
    }
    
    // Extract actual tags from API for Tags section
    const apiTags: string[] = [];
    if (item.tags && Array.isArray(item.tags)) {
      apiTags.push(...item.tags);
    }
    
    // Extract percentage (data readiness or quality score)
    const percentage = item.percentage || item.dataReadiness || undefined;
    
    // Extract file format
    const fileFormat = item.fileFormat || item.format || '';
    const fileType = fileFormat || '';
    
    // Extract date using card format
    const date = this.formatService.formatDateForCard(item.itemCreatedAt || item.createdAt || new Date());
    
    // Extract badge/status using helper service
    const badge = this.helperService.extractAccessBadge(item);
    const badgeColor = this.helperService.getBadgeColorVariant(badge);
    
    // Check if item is new (created within last 7 days)
    const isNew = this.isRecentItem(item.itemCreatedAt || item.createdAt);
    
    // Build link based on asset type
    const link = `/dataset/${id}`;

    // Extract metrics data from metrics object - always include metrics object
    const metricsData = item.metrics || {};
    const metrics = {
      likes: metricsData.likes ?? item.likeCount ?? item.likes ?? item.like_count ?? 0,
      downloads: metricsData.downloads ?? item.downloadCount ?? item.downloads ?? item.download_count ?? 0,
      files: item.fileCount || item.files || item.file_count || 0,
      views: metricsData.views ?? item.viewCount ?? item.views ?? item.view_count ?? 0,
      dislikes: metricsData.dislikes ?? 0
    };

    return {
      id,
      title,
      shortDescription,
      organization,
      tags: resourceServerTags.slice(0, 3), // ResourceServer names for card display (STAC, OGC, FILE)
      serverAccessTypes: serverAccessTypes.length > 0 ? serverAccessTypes : undefined,
      apiTags: apiTags, // Actual tags from API for Tags section
      percentage,
      fileType,
      date,
      badge,
      badgeColor,
      isNew,
      link,
      metrics: metrics // Always include metrics object
    };
  }

  /**
   * Check if item was created within last 7 days
   */
  private isRecentItem(dateString: string | Date): boolean {
    if (!dateString) return false;
    const itemDate = new Date(dateString);
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return itemDate >= weekAgo;
  }

  /**
   * Build searchCriteria array from selected filters
   */
  private buildSearchCriteriaFromFilters(): SearchCriteria[] {
    const searchCriteria: SearchCriteria[] = [];

    // Process each filter section
    this.filterConfig.sections.forEach(section => {
      if (!section.options || section.options.length === 0) {
        return;
      }

      // Get checked options
      const checkedOptions = section.options.filter(opt => opt.checked);
      if (checkedOptions.length === 0) {
        return;
      }

      const values = checkedOptions.map(opt => opt.label);

      // Map section IDs to API field names and search types
      switch (section.id) {
        case 'tags':
          searchCriteria.push({
            searchType: 'term',
            field: 'tags',
            values: values
          });
          break;

        case 'concepts':
          searchCriteria.push({
            searchType: 'term',
            field: 'concepts',
            values: values
          });
          break;

        case 'file-format':
          searchCriteria.push({
            searchType: 'term',
            field: 'fileFormat',
            values: values
          });
          break;

        case 'permissions':
          // Map permission display labels back to API values
          const apiPermissionValues = checkedOptions.map(opt => {
            // Use originalValue if available (stored during filter population)
            if ((opt as any).originalValue) {
              return (opt as any).originalValue;
            }
            // Otherwise map display labels to API values
            const lowerVal = opt.label.toLowerCase();
            if (lowerVal === 'public') return 'open';
            if (lowerVal === 'restricted') return 'restricted';
            if (lowerVal === 'personal') return 'pii';
            return lowerVal;
          });
          searchCriteria.push({
            searchType: 'term',
            field: 'accessPolicy',
            values: apiPermissionValues
          });
          break;

        case 'resource-server':
          searchCriteria.push({
            searchType: 'flattenedTerm',
            field: 'resourceServer.url',
            values: values
          });
          break;

        case 'last-updated':
          // Handle date range for last updated
          const dateRange = this.getDateRangeFromLastUpdated(checkedOptions);
          if (dateRange) {
            searchCriteria.push({
              searchType: 'betweenTemporal',
              field: 'itemCreatedAt',
              values: [dateRange.start, dateRange.end]
            });
          }
          break;

        case 'organisation':
          searchCriteria.push({
            searchType: 'term',
            field: 'organization',
            values: values
          });
          break;

        case 'organisation-type':
          searchCriteria.push({
            searchType: 'term',
            field: 'organizationType',
            values: values
          });
          break;
      }
    });

    return searchCriteria;
  }

  /**
   * Get date range from last updated filter options
   */
  private getDateRangeFromLastUpdated(checkedOptions: any[]): { start: string; end: string } | null {
    if (checkedOptions.length === 0) {
      return null;
    }

    // Get the most recent date range (if multiple selected, use the most recent)
    const now = new Date();
    let startDate: Date | null = null;
    const endDate = new Date(now);

    // Find the most recent date range
    checkedOptions.forEach(option => {
      let daysAgo = 0;
      switch (option.id) {
        case '1-day':
          daysAgo = 1;
          break;
        case '1-week':
          daysAgo = 7;
          break;
        case '1-month':
          daysAgo = 30;
          break;
        case '1-year':
          daysAgo = 365;
          break;
      }

      if (daysAgo > 0) {
        const optionStartDate = new Date(now);
        optionStartDate.setDate(optionStartDate.getDate() - daysAgo);
        
        // Use the most recent start date (smallest daysAgo)
        if (!startDate || optionStartDate > startDate) {
          startDate = optionStartDate;
        }
      }
    });

    if (!startDate) {
      return null;
    }

    // Format dates as ISO strings with timezone offset
    const formatDateWithTimezone = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      
      // Get timezone offset
      const offset = -date.getTimezoneOffset();
      const offsetHours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, '0');
      const offsetMinutes = String(Math.abs(offset) % 60).padStart(2, '0');
      const offsetSign = offset >= 0 ? '+' : '-';
      
      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${offsetSign}${offsetHours}${offsetMinutes}`;
    };

    // At this point, startDate is guaranteed to be non-null due to the check above
    const finalStartDate: Date = startDate;
    finalStartDate.setHours(0, 0, 0, 0);
    // Set end date to end of day
    endDate.setHours(23, 59, 59, 999);

    return {
      start: formatDateWithTimezone(finalStartDate),
      end: formatDateWithTimezone(endDate)
    };
  }

  /**
   * Update pagination from API response paginationInfo
   * Handles dynamic pagination based on totalCount from API
   */
  private updatePaginationFromResponse(response: any): void {
    // Check for paginationInfo at root level
    if (response?.paginationInfo) {
      const paginationInfo = response.paginationInfo;
      
      // Use totalPages if available, otherwise calculate from totalCount
      if (paginationInfo.totalPages !== undefined) {
        this.totalPages = paginationInfo.totalPages;
      } else if (paginationInfo.totalCount !== undefined) {
        this.totalPages = Math.ceil(paginationInfo.totalCount / this.itemsPerPage);
      }
      
      // Update currentPage if provided in paginationInfo
      if (paginationInfo.page !== undefined) {
        this.currentPage = paginationInfo.page;
      }
    }
    // Fallback: Check for totalCount in result object
    else if (response?.result?.totalCount !== undefined) {
      this.totalPages = Math.ceil(response.result.totalCount / this.itemsPerPage);
    }
    // Fallback: Check for totalCount at root level
    else if (response?.totalCount !== undefined) {
      this.totalPages = Math.ceil(response.totalCount / this.itemsPerPage);
    }
    // Fallback: Check for totalPages in result object
    else if (response?.result?.totalPages !== undefined) {
      this.totalPages = response.result.totalPages;
    }
    // Fallback: Check for totalPages at root level
    else if (response?.totalPages !== undefined) {
      this.totalPages = response.totalPages;
    }
  }

  /**
   * Load mock datasets as fallback
   */
  private loadMockDatasets(): void {
    this.datasets = [
      {
        id: '1',
        title: 'District wise Year wise Drip & Sprinkler Irrigation Info in Maharashtra State',
        organization: 'Agromet Advisory Service Division, IMD.',
        tags: ['STAC', 'OGC', 'FILE'],
        percentage: 95.77,
        fileType: 'CSV',
        date: 'Jun 22, 2025',
        badge: 'Public',
        badgeColor: 'public',
        isNew: true,
        link: '/dataset/1',
        metrics: {
          likes: 5,
          downloads: 1071,
          files: 0,
          views: 4995
        }
      },
      {
        id: '2',
        title: 'District wise Year wise Drip & Sprinkler Irrigation Info in Maharashtra State',
        organization: 'Agromet Advisory Service Division, IMD.',
        tags: ['STAC', 'OGC', 'FILE'],
        percentage: 96.77,
        fileType: 'CSV',
        date: 'Jun 22, 2025',
        badge: 'Public',
        badgeColor: 'public',
        link: '/dataset/2',
        metrics: {
          likes: 3,
          downloads: 856,
          files: 2,
          views: 3240
        }
      },
      {
        id: '3',
        title: 'District-wise yearly Rain-water Info in Maharashtra',
        organization: 'Maharashtra Agriculture Department',
        tags: ['STAC', 'OGC', 'FILE'],
        percentage: 94.50,
        fileType: 'CSV',
        date: 'Jun 20, 2025',
        badge: 'Public',
        badgeColor: 'public',
        link: '/dataset/3',
        metrics: {
          likes: 8,
          downloads: 1245,
          files: 1,
          views: 6120
        }
      },
      {
        id: '4',
        title: 'Crop Yield Data for Maharashtra Districts',
        organization: 'ICAR - Indian Council of Agricultural Research',
        tags: ['STAC', 'OGC', 'FILE'],
        percentage: 97.25,
        fileType: 'CSV',
        date: 'Jun 18, 2025',
        badge: 'Public',
        badgeColor: 'public',
        link: '/dataset/4'
      },
      {
        id: '5',
        title: 'Soil Quality Metrics Across Maharashtra',
        organization: 'Department of Agriculture, GoM',
        tags: ['STAC', 'OGC', 'FILE'],
        percentage: 93.80,
        fileType: 'CSV',
        date: 'Jun 15, 2025',
        badge: 'Public',
        badgeColor: 'public',
        link: '/dataset/5'
      },
      {
        id: '6',
        title: 'Weather Station Data - Maharashtra Region',
        organization: 'IMD - India Meteorological Department',
        tags: ['STAC', 'OGC', 'FILE'],
        percentage: 98.10,
        fileType: 'CSV',
        date: 'Jun 12, 2025',
        badge: 'Public',
        badgeColor: 'public',
        link: '/dataset/6'
      },
      // Add more datasets as needed
    ];
    // Update totalPages based on mock data length
    this.totalPages = Math.ceil(this.datasets.length / this.itemsPerPage);
  }

  onFilterChange(filterConfig: FilterConfig): void {
    this.filterConfig = filterConfig;
    this.updateActiveFilterCount();
    this.currentPage = 1; // Reset to first page when filters change
    // Reload datasets based on filters
    this.loadDatasets();
  }

  onResetFilters(): void {
    this.filterConfig.sections.forEach(section => {
      if (section.options) {
        section.options.forEach(option => {
          option.checked = false;
        });
      }
    });
    this.updateActiveFilterCount();
    // TODO: Reload datasets
    this.loadDatasets();
  }

  openMobileFilter(): void {
    console.log('openMobileFilter called, current state:', this.isMobileFilterOpen);
    this.isMobileFilterOpen = true;
    console.log('Filter modal should now be open:', this.isMobileFilterOpen);
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
  }

  closeMobileFilter(): void {
    console.log('closeMobileFilter called');
    this.isMobileFilterOpen = false;
    // Restore body scroll
    document.body.style.overflow = '';
  }

  onSearchChange(query: string): void {
    const trimmedQuery = query.trim();
    
    // Only call search API if query has at least 3 characters
    if (trimmedQuery.length >= 3) {
      this.searchQuery = trimmedQuery;
      this.currentPage = 1; // Reset to first page when searching
      // Reload datasets with search query
      this.loadDatasets();
    } else if (trimmedQuery.length === 0) {
      // If search is cleared, reset and load all datasets
      this.searchQuery = '';
      this.currentPage = 1;
      this.loadDatasets();
    }
    // If query is 1-2 characters, don't call API (wait for more input)
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    // TODO: Load page data
    this.loadDatasets();
  }

  onViewModeChange(mode: 'grid' | 'list' | 'table'): void {
    this.viewMode = mode;
  }

  onItemsPerPageChange(itemsPerPage: number): void {
    this.itemsPerPage = itemsPerPage;
    this.currentPage = 1; // Reset to first page when changing items per page
    // Reload datasets with new page size
    this.loadDatasets();
  }

  onSortChange(sortOption: string): void {
    this.sortOption = sortOption;
    this.currentPage = 1; // Reset to first page when sort changes
    this.loadDatasets();
  }

  /** Map UI sort option to API sort string */
  private getSortStringFromOption(option: string): string {
    switch (option) {
      case 'a-z':
        return 'name:asc;';
      case 'z-a':
        return 'name:desc;';
      case 'new-to-old':
        return 'itemCreatedAt:desc;';
      case 'old-to-new':
        return 'itemCreatedAt:asc;';
      default:
        return 'itemCreatedAt:desc;';
    }
  }

  private updateActiveFilterCount(): void {
    let count = 0;
    this.filterConfig.sections.forEach(section => {
      if (section.options) {
        count += section.options.filter(opt => opt.checked).length;
      }
    });
    this.filterConfig.activeFilterCount = count;
  }

  /**
   * Load filter options from the API
   */
  private async loadFilterOptions(): Promise<void> {
    try {
      // Build search criteria for datasets (DataBank type)
      const searchCriteria: SearchCriteria[] = [
        {
          searchType: 'term',
          field: 'type',
          values: ['adex:DataBank']
        }
      ];

      // Define the filter fields to retrieve (must match API: searchCriteria + filter)
      const filterFields = [
        'concepts',
        'tags',
        'organization',
        'fileFormat',
        'organizationType',
        'accessPolicy',
        'resourceServer.url'
      ];

      // Call the API to get filter options
      const response = await this.catalogueApiService.getFilterList(searchCriteria, filterFields);

      // Map the response to populate filter sections
      if (response?.result) {
        this.populateFilterSections(response.result);
      }
    } catch (error) {
      console.error('Failed to load filter options:', error);
    }
  }

  /**
   * Populate filter sections with data from API response
   * Handles both array of objects and single object with arrays
   */
  private populateFilterSections(apiData: any): void {
    // Helper function to extract unique values from array of objects or use array directly
    const extractValues = (data: any, field: string): string[] => {
      if (!data) return [];
      
      // If it's already an array of strings, return unique values
      if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'string') {
        return [...new Set(data.filter((v: any) => v !== null && v !== undefined && v !== ''))];
      }
      
      // If it's an array of objects, extract unique values from the field
      if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object') {
        const values: string[] = [];
        data.forEach((item: any) => {
          // Handle nested fields like "resourceServer.url"
          const fieldParts = field.split('.');
          let value = item;
          for (const part of fieldParts) {
            value = value?.[part];
          }
          
          // Handle array values (e.g., tags, concepts might be arrays)
          if (Array.isArray(value)) {
            values.push(...value.filter((v: any) => v !== null && v !== undefined && v !== ''));
          } else if (value !== null && value !== undefined && value !== '') {
            values.push(String(value));
          }
        });
        return [...new Set(values)];
      }
      
      // If it's a single object with the field
      if (typeof data === 'object' && !Array.isArray(data)) {
        const fieldParts = field.split('.');
        let value = data;
        for (const part of fieldParts) {
          value = value?.[part];
        }
        
        if (Array.isArray(value)) {
          return [...new Set(value.filter((v: any) => v !== null && v !== undefined && v !== ''))];
        } else if (value !== null && value !== undefined && value !== '') {
          return [String(value)];
        }
      }
      
      return [];
    };

    // Helper function to populate a filter section
    const populateSection = (sectionId: string, values: string[]) => {
      if (values.length === 0) return;
      
      const section = this.filterConfig.sections.find(s => s.id === sectionId);
      if (section) {
        section.options = values.map((value: string, index: number) => ({
          id: `${sectionId}-${index}`,
          label: value,
          checked: false
        }));
      }
    };

    // Extract and populate concepts
    const concepts = extractValues(apiData, 'concepts');
    populateSection('concepts', concepts);

    // Extract and populate tags
    const tags = extractValues(apiData, 'tags');
    populateSection('tags', tags);

    // Extract and populate organization -> organisation (Departments filter)
    const departments = extractValues(apiData, 'organization');
    populateSection('organisation', departments);

    // Extract and populate fileFormat -> file-format
    const fileFormats = extractValues(apiData, 'fileFormat');
    populateSection('file-format', fileFormats);

    // Extract and populate organizationType -> organisation-type
    const orgTypes = extractValues(apiData, 'organizationType');
    populateSection('organisation-type', orgTypes);

    // Extract and populate accessPolicy -> permissions with display mapping
    const accessPolicies = extractValues(apiData, 'accessPolicy');
    if (accessPolicies.length > 0) {
      const section = this.filterConfig.sections.find(s => s.id === 'permissions');
      if (section) {
        // Map API values to display labels
        const permissionMapping: { [key: string]: string } = {
          'open': 'Public',
          'restricted': 'Restricted',
          'pii': 'Personal'
        };
        
        section.options = accessPolicies.map((value: string, index: number) => {
          const displayLabel = permissionMapping[value.toLowerCase()] || value;
          return {
            id: `permissions-${index}`,
            label: displayLabel, // Display label (Public, Restricted, Personal)
            checked: false,
            // Store original API value for filtering
            originalValue: value
          } as any;
        });
      }
    }

    // Extract and populate resourceServer.url -> resource-server
    const resourceServers = extractValues(apiData, 'resourceServer.url');
    populateSection('resource-server', resourceServers);

    // Update active filter count after populating
    this.updateActiveFilterCount();
  }
}

